import React from 'react';
import classNames from 'classnames';
import sortBy from '@betgames/bg-tools/dist/utils/sortBy';
import { SortDirection } from '@betgames/bg-tools/dist/enums/SortDirection';
import { EMPTY_SPACE } from 'twain-sport-fe-mono/constants/textSymbols';
import {
    IParticipant,
    IStandingsMatch,
    IStandingsParticipant,
} from 'twain-sport-fe-mono/interfaces';
import { ChevronIcon, ChevronDirection } from 'twain-sport-fe-mono/icons';
import { useParticipantsByCode } from 'twain-sport-fe-mono/hooks/useParticipantsByCode';
import { ParticipantsTable, IStatisticsColumn } from '../ParticipantsTable';
import { ParticipantRow } from '../ParticipantRow';
import { StandingsMatch } from '../StandingsMatch';
import classes from './StandingsGroups.module.scss';

interface IProps {
    participants: IStandingsParticipant[];
    matches: IStandingsMatch[];
    navigate: (participantCode: string) => void;
}

const statisticsOptions = ['wins', 'loses', 'accuracy', 'points'];

const statisticsColumns: IStatisticsColumn[] = [
    { name: 'win', shortName: 'w' },
    { name: 'loss', shortName: 'l' },
    { name: 'accuracy', shortName: 'acc%' },
    { name: 'points', shortName: 'pts' },
];

export const StandingsGroups: React.FC<IProps> = (props) => {
    const [expandedParticipantsInfo, setParticipantsInfo] = React.useState<IParticipant['code'][]>(
        [],
    );
    const participantsByCode = useParticipantsByCode(props.participants);

    const toggleParticipantsInfo = React.useCallback((code: IParticipant['code']) => {
        setParticipantsInfo((state) => {
            const index = state.findIndex((item) => item === code);
            const newState = [...state];

            if (index !== -1) {
                newState.splice(index, 1);
            } else {
                newState.push(code);
            }

            return newState;
        });
    }, []);

    return (
        <ParticipantsTable
            firstColumnText={EMPTY_SPACE}
            statisticsColumns={statisticsColumns}
            className={classes.table}
        >
            {props.participants
                .sort(sortBy((participant) => participant.groupPosition, SortDirection.ASC))
                .map((participant, index) => {
                    const isExpandedInfo = expandedParticipantsInfo.includes(participant.code);

                    const matches = isExpandedInfo
                        ? props.matches.filter(
                              (match) =>
                                  match.participants.left.code === participant.code ||
                                  match.participants.right.code === participant.code,
                          )
                        : [];

                    return [
                        <ParticipantRow
                            key={participant.code}
                            participant={participant}
                            index={index + 1}
                            mainStatistics={statisticsOptions}
                            className={classNames(classes.participant, {
                                [classes.expanded]: isExpandedInfo,
                            })}
                            onActionClick={() => {
                                toggleParticipantsInfo(participant.code);
                            }}
                            hideRank
                            renderAction={() => (
                                <ChevronIcon
                                    direction={
                                        isExpandedInfo ? ChevronDirection.UP : ChevronDirection.DOWN
                                    }
                                    className={classes.chevronIcon}
                                />
                            )}
                            navigate={() => props.navigate(participant.code)}
                            forceFormatStatsValue
                        />,
                        matches.map((match) => (
                            <tr key={match.code}>
                                <td colSpan={8} className={classes.match}>
                                    <StandingsMatch
                                        participants={{
                                            left: participantsByCode[match.participants.left.code],
                                            right: participantsByCode[
                                                match.participants.right.code
                                            ],
                                        }}
                                        match={match}
                                        className={classes.standingsMatch}
                                    />
                                </td>
                            </tr>
                        )),
                    ];
                })}
        </ParticipantsTable>
    );
};
